/**
 * Web deploy tier for analytics / Bugsnag.
 *
 * UAT and production both use Vite `production` mode (same baked env), so hostname
 * is the source of truth in the browser. Unmatched hosts (localhost, ngrok, etc.)
 * are `development`. Vite mode / `VITE_RELEASE_STAGE` apply only when there is no
 * hostname (tests / non-browser).
 */
export type BuxusReleaseStage = 'development' | 'staging' | 'uat' | 'production'

const STAGES: ReadonlySet<string> = new Set([
  'development',
  'staging',
  'uat',
  'production',
])

function asStage(value: string | null | undefined): BuxusReleaseStage | null {
  const v = value?.trim().toLowerCase()
  if (v && STAGES.has(v)) return v as BuxusReleaseStage
  return null
}

function isLocalOrTunnelHost(host: string): boolean {
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.local') ||
    host.endsWith('.localhost') ||
    // Local tunnels — never used for staging/UAT/prod deploys.
    host.includes('ngrok') ||
    host.endsWith('.loca.lt') ||
    host.includes('localtunnel')
  )
}

/**
 * Map the public web host to a release stage.
 * Returns `development` for localhost, tunnels, and any host that is not a known
 * staging / UAT / production deploy hostname.
 */
export function releaseStageFromHostname(hostname: string): BuxusReleaseStage | null {
  const host = hostname.trim().toLowerCase()
  if (!host) return null

  // Known Azure / custom domains first (uat before staging; both before production).
  if (host.includes('uat')) return 'uat'
  if (host.includes('staging')) return 'staging'

  if (
    host === 'app.buxus.io' ||
    host === 'buxus-apps-web.azurewebsites.net' ||
    (host.endsWith('.buxus.io') && !isLocalOrTunnelHost(host))
  ) {
    return 'production'
  }

  // localhost, ngrok, and any other unmatched browser host → development
  return 'development'
}

export function resolveWebReleaseStage(options?: {
  hostname?: string | null
  viteMode?: string | null
  envOverride?: string | null
}): BuxusReleaseStage {
  const hostname =
    options?.hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')

  if (hostname && hostname.trim()) {
    return releaseStageFromHostname(hostname.trim()) ?? 'development'
  }

  // No hostname (unit tests / non-browser): env override, then Vite mode.
  const fromEnv = asStage(options?.envOverride)
  if (fromEnv) return fromEnv

  const mode = (options?.viteMode ?? '').trim().toLowerCase()
  if (mode === 'staging') return 'staging'
  if (mode === 'production') return 'production'
  if (mode === 'development') return 'development'

  return 'development'
}
